import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  ArrowLeft,
  Heart,
  Shield,
  Truck,
  Star,
} from "lucide-react";
import "./CartPage.css";
import { useMemo } from "react";
import OrderSummary from "../OrderSummery/OrderSummary";
import AddressPage from "../addresspage/AddressPage";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../utils/axiosConfig";
import Swal from "sweetalert2";
import { useCart } from "../../context/CartContext";
const CartPage = () => {
  const navigate = useNavigate();

  // Cart state
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [savedItems, setSavedItems] = useState([]);
  const [imageLoaded, setImageLoaded] = useState({});
  const [currentStep, setCurrentStep] = useState("cart");
  // Auth
  const { isAuthenticated, accessToken } = useAuth();
  const { fetchCartData, removeFromCart } = useCart();
  // Delivery state - simplified (no location tracking)
  const [deliveryData, setDeliveryData] = useState({
    deliveryCharge: 0,
    isFreeDelivery: false,
    isDeliverable: true,
    calculatedAtCheckout: true,
  });

  // ============ COUPON STATE ============
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState("");
  const [couponMessage, setCouponMessage] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);

  // Address form state
  const [addressForm, setAddressForm] = useState({
    fullName: "",
    contactNumber: "",
    email: "",
    address: "",
    pincode: "",
    state: "",
    landmark: "",
  });

  const removeCoupon = () => {
    setSelectedCoupon(""); // Reset to empty string
    setAppliedDiscount(0);
    setCouponMessage("");
  };

  // Add this debug effect to see the coupon data flow
  useEffect(() => {
    console.log("🔍 DEBUG - selectedCoupon in CartPage:", selectedCoupon);
    console.log("🔍 DEBUG - Type of selectedCoupon:", typeof selectedCoupon);
  }, [selectedCoupon]);

  // ============ FETCH CART ITEMS ============
  useEffect(() => {
    const fetchCartItems = async () => {
      if (!isAuthenticated || !accessToken) {
        console.log("User not logged in, cart is empty");
        setCartItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get("/cart");

        console.log("Cart response:", response.data);

        if (response.data.success) {
          const items = response.data.items || [];

          const processedItems = items.map((item) => ({
            ...item,
            productId: item.productId._id || item.productId.id || item.productId,
            gst: Number(item.gst) || 0,
            price: Number(item.price) || 0,
            quantity: Number(item.quantity) || 1,
          }));

          setCartItems(processedItems);
          calculateTotals(processedItems);
        } else {
          console.error("Cart fetch failed:", response.data);
          setCartItems([]);
        }
      } catch (error) {
        console.error("Error fetching cart items:", error);

        if (error.response?.data?.code === "TOKEN_EXPIRED") {
          console.log("Token expired, axios will handle refresh");
        } else if (error.response?.status === 401) {
          console.log("User not authenticated");
          setCartItems([]);
        } else {
          alert("Failed to load cart. Please try again.");
        }

        setCartItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, [isAuthenticated, accessToken]);

  // ============ FETCH COUPONS ============
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/coupons`
        );
        const data = await response.json();

        if (data.success) {
          setCoupons(data.data || []);
        }
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    };

    fetchCoupons();
  }, []);

  // ============ CALCULATE TOTALS ============
  const calculateTotals = (items) => {
    const availableItems = items.filter((item) => item.inStock !== false);

    const subtotal = availableItems.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = parseInt(item.quantity) || 1;
      return sum + itemPrice * itemQuantity;
    }, 0);

    const totalTaxAmount = availableItems.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = parseInt(item.quantity) || 1;
      const gstRate = parseFloat(item.gst) || 0;
      const itemSubTotal = itemPrice * itemQuantity;
      const itemTax = itemSubTotal * (gstRate / 100);

      return sum + itemTax;
    }, 0);

    const itemCount = availableItems.reduce(
      (sum, item) => sum + (parseInt(item.quantity) || 0),
      0
    );

    setTotalAmount(subtotal);
    setTotalTax(totalTaxAmount);
    setTotalItems(itemCount);
  };

  // ============ APPLY COUPON ============
  // ============ APPLY COUPON ============
  const applyCoupon = async () => {
    if (!selectedCoupon.trim()) {
      setCouponMessage("Please select a coupon code");
      return;
    }

    setCouponLoading(true);
    setCouponMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/coupons/validate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: selectedCoupon,
            cartTotal: totalAmount,
            totalQuantity: totalItems,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        const discount = Math.round(
          totalAmount * (data.discountPercentage / 100)
        );
        setAppliedDiscount(discount);

        // ✅ FIX: Store the FULL coupon object, not just the code
        setSelectedCoupon({
          code: selectedCoupon, // The coupon code string
          discountPercentage: data.discountPercentage, // The percentage from API response
        });

        setCouponMessage(
          `✓ Coupon applied! ${data.discountPercentage}% discount saved`
        );
      } else {
        setCouponMessage(data.message || "Invalid coupon");
        setAppliedDiscount(0);
        setSelectedCoupon(""); // Reset to empty string if invalid
      }
    } catch (error) {
      console.error("Error validating coupon:", error);
      setCouponMessage("Failed to validate coupon. Please try again.");
      setAppliedDiscount(0);
      setSelectedCoupon(""); // Reset to empty string on error
    } finally {
      setCouponLoading(false);
    }
  };

  // ============ CALCULATE FINAL TOTAL ============
  const calculateFinalTotal = () => {
    const discount = getDiscountAmount();
    const deliveryCharge = deliveryData?.deliveryCharge || 0;
    return Math.round(totalAmount + totalTax + deliveryCharge - discount);
  };

  // ============ GET DISCOUNT AMOUNT ============
  const getDiscountAmount = () => {
    return appliedDiscount > 0
      ? appliedDiscount
      : Math.round(totalAmount * 0.1);
  };

  // ============ GET TAX BREAKDOWN ============
  const getTaxBreakdown = () => {
    const availableItems = cartItems.filter((item) => item.inStock !== false);
    const taxBreakdown = {};

    availableItems.forEach((item) => {
      const gstRate = parseFloat(item.gst) || 0;
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = parseInt(item.quantity) || 1;

      if (gstRate > 0) {
        const itemSubTotal = itemPrice * itemQuantity;
        const itemTax = itemSubTotal * (gstRate / 100);

        if (!taxBreakdown[gstRate]) {
          taxBreakdown[gstRate] = 0;
        }
        taxBreakdown[gstRate] += itemTax;
      }
    });

    return taxBreakdown;
  };

  // ============ IMAGE HANDLING ============
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    const baseUrl = process.env.REACT_APP_BASE_URL?.split("/api")[0] || "";
    return `${baseUrl}/${imagePath.replace(/^\//, "")}`;
  };

  const handleImageLoad = (productId) => {
    setImageLoaded((prev) => ({ ...prev, [productId]: true }));
  };

  const handleImageError = (productId, e) => {
    e.target.onerror = null;
    e.target.src = "https://via.placeholder.com/150?text=No+Image";
    setImageLoaded((prev) => ({ ...prev, [productId]: true }));
  };

  // ============ CART OPERATIONS ============
  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;

    setLoading(true);
    try {
      const response = await api.put("/cart/update", {
        productId,
        quantity: newQuantity,
      });

      if (response.data.success) {
        const updatedItems =
          response.data.items ||
          cartItems.map((item) =>
            item.productId === productId
              ? { ...item, quantity: newQuantity }
              : item
          );

        setCartItems(updatedItems);
        calculateTotals(updatedItems);
      } else {
        throw new Error("Failed to update quantity");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      alert(error.response?.data?.message || "Failed to update quantity");
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    setLoading(true);
    try {
      const response = await api.delete("/cart/remove", {
        data: { productId },
      });

      if (response.data.success) {
        const updatedItems =
          response.data.items ||
          cartItems.filter((item) => item.productId !== productId);

        // Update the cart page UI
        setCartItems(updatedItems);
        calculateTotals(updatedItems);

        // 🔥 VERY IMPORTANT — updates the NAVBAR BADGE instantly
        fetchCartData();
      } else {
        throw new Error("Failed to remove item");
      }
    } catch (error) {
      console.error("Error removing item:", error);
      alert(error.response?.data?.message || "Failed to remove item");
    } finally {
      setLoading(false);
    }
  };

  const saveForLater = (item) => {
    setSavedItems([...savedItems, item]);
    removeItem(item.productId);

  };

  const clearCart = async () => {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      setLoading(true);
      try {
        await api.delete("/cart/clear");

        setCartItems([]);
        setTotalAmount(0);
        setTotalTax(0);
        setTotalItems(0);
      } catch (error) {
        console.error("Error clearing cart:", error);
        alert(error.response?.data?.message || "Failed to clear cart");
      } finally {
        setLoading(false);
      }
    }
  };

  // ============ NAVIGATION ============
  const handleBackToShopping = () => {
    navigate("/");
  };

  const handleBackToCart = () => {
    setCurrentStep("cart");
  };

  // ============ ORDER PLACEMENT ============
  const handlePlaceOrder = () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty. Please add items before placing an order.");
      return;
    }

    const outOfStockItems = cartItems.filter((item) => !item.inStock);
    if (outOfStockItems.length > 0) {
      alert(
        "Some items in your cart are out of stock. Please remove them before placing an order."
      );
      return;
    }

    setCurrentStep("address");
    navigate("/address");
  };

  // ============ ADDRESS FORM HANDLERS ============
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (
      !addressForm.fullName ||
      !addressForm.contactNumber ||
      !addressForm.email ||
      !addressForm.address ||
      !addressForm.pincode ||
      !addressForm.state
    ) {
      alert("Please fill in all required fields");
      return;
    }

    setCurrentStep("payment");
  };

  // ============ UTILITY FUNCTIONS ============
  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={12}
        className={i < Math.floor(rating) ? "star-filled" : "star-empty"}
      />
    ));
  };

  // ============ DELIVERY INFO COMPONENT ============
  const DeliveryInfoDisplay = () => {
    return (
      <div className="delivery-info-section">
        <div className="delivery-main-info">
          {/* <Truck size={18} /> */}
          <div className="delivery-text">
            {/* <span className="delivery-label">Delivery Charges</span> */}
          </div>
        </div>
      </div>
    );
  };

  // ============ EMPTY CART STATE ============
  if (cartItems.length === 0 && currentStep === "cart") {
    return (
      <div className="cart-container">
        <div className="empty-cart">
          <div className="empty-cart-content">
            <div className="empty-cart-icon">
              <ShoppingBag size={80} />
            </div>
            <h2 className="empty-cart-title">Your Cart is Empty</h2>
            <p className="empty-cart-subtitle">
              Discover amazing products and add them to your cart!
            </p>
            <button
              onClick={handleBackToShopping}
              className="btn-primary large"
            >
              Start Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ CALCULATE VALUES ============
  const discount = getDiscountAmount();
  const finalTotal = calculateFinalTotal();

  // ============ RENDER ============
  return (
    <div className="cart-container">
      <div className="cart-wrapper">
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-left">
            <button
              onClick={
                currentStep === "cart" ? handleBackToShopping : handleBackToCart
              }
              className="btn-back"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="cart-title-section">
              <h1 className="cart-title">
                {currentStep === "cart"
                  ? "Shopping Cart"
                  : currentStep === "address"
                    ? "Shipping Address"
                    : "Payment"}
              </h1>
            </div>
          </div>
          {currentStep === "cart" && (
            <button
              onClick={clearCart}
              className="btn-clear"
              disabled={loading}
            >
              Clear Cart
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className={`progress-step ${currentStep === "cart" ? "active" : ""
                }`}
            >
              <div className="progress-circle">1</div>
              <span>Shopping Cart</span>
            </div>
            <div
              className={`progress-step ${currentStep === "address" ? "active" : ""
                }`}
            >
              <div className="progress-circle">2</div>
              <span>Address</span>
            </div>
            <div
              className={`progress-step ${currentStep === "payment" ? "active" : ""
                }`}
            >
              <div className="progress-circle">3</div>
              <span>Payment</span>
            </div>
          </div>
        </div>

        {/* Cart Step */}
        {currentStep === "cart" && (
          <div className="cart-content">
            {/* Cart Items */}
            <div className="cart-items-container">
              <div className="cart-items-header">
                <h2>Your Items</h2>
                <DeliveryInfoDisplay />
              </div>

              <div className="cart-items">
                {cartItems.map((item) => {
                  const gstRate = parseFloat(item.gst || 0);
                  const itemSubtotal = item.price * item.quantity;
                  const itemGstAmount = itemSubtotal * (gstRate / 100);

                  return (
                    <div
                      key={item._id}
                      className={`cart-item ${!item.inStock ? "out-of-stock" : ""
                        }`}
                    >
                      {!item.inStock && (
                        <div className="out-of-stock-badge">Out of Stock</div>
                      )}

                      <div className="item-image-container">
                        <img
                          src={getImageUrl(item.productImage)}
                          alt={item.productName}
                          className={`item-image ${imageLoaded[item.productId] ? "" : "loading"
                            }`}
                          onLoad={() => handleImageLoad(item.productId)}
                          onError={(e) => handleImageError(item.productId, e)}
                        />
                        {item.inStock && (
                          <div className="stock-indicator"></div>
                        )}
                      </div>

                      <div className="item-details">
                        <div className="item-header">
                          <h3 className="item-name">{item.productName}</h3>
                        </div>

                        <p className="item-category">{item.productCategory}</p>

                        <div className="item-price-section">
                          <span className="item-price">
                            ₹{item.price.toLocaleString()}
                          </span>
                          <span className="price-per-unit">per unit</span>
                          {item.gst > 0 && (
                            <span className="tax-rate">
                              (Includes {item.gst}% GST)
                            </span>
                          )}
                        </div>

                        <div className="item-controls">
                          <div className="quantity-controls">
                            <button
                              onClick={() =>
                                updateQuantity(
                                  item.productId._id ||
                                  item.productId.id ||
                                  item.productId,
                                  item.quantity - 1
                                )
                              }
                              className="quantity-btn"
                              disabled={
                                loading || item.quantity <= 1 || !item.inStock
                              }
                            >
                              <Minus size={16} />
                            </button>
                            <span className="quantity-display">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                updateQuantity(
                                  item.productId._id ||
                                  item.productId.id ||
                                  item.productId,
                                  item.quantity + 1
                                )
                              }
                              className="quantity-btn"
                              disabled={loading || !item.inStock}
                            >
                              <Plus size={16} />
                            </button>
                          </div>

                          <div className="item-actions">
                            <button
                              onClick={() => removeItem(item.productId)}
                              className="action-btn remove-btn"
                              disabled={loading}
                            >
                              <Trash2 size={18} />
                              <span>Remove</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="item-total">
                        <div className="item-rating">
                          {renderStars(item.rating)}
                          <span className="rating-text">({item.rating})</span>
                        </div>
                        <div className="item-subtotal">
                          <span className="subtotal-label">Subtotal</span>
                          <span className="subtotal-amount">
                            ₹{itemSubtotal.toLocaleString()}
                          </span>
                        </div>
                        {gstRate > 0 && (
                          <div className="item-tax">
                            <span className="tax-label">GST ({gstRate}%)</span>
                            &nbsp;
                            <span className="tax-amount">
                              ₹{Math.round(itemGstAmount).toLocaleString()}
                            </span>
                          </div>
                        )}
                        <div className="item-final-total">
                          <span className="final-total-label">
                            Total :&nbsp;
                          </span>
                          <span className="final-total-amount">
                            ₹
                            {Math.round(
                              itemSubtotal + itemGstAmount
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order Summary */}
            <OrderSummary
              totalItems={totalItems}
              totalAmount={totalAmount}
              totalTax={totalTax}
              deliveryCharge={deliveryData?.deliveryCharge || 0}
              deliveryLoading={false}
              deliveryError={null}
              deliveryData={deliveryData}
              coupons={coupons}
              selectedCoupon={selectedCoupon}
              setSelectedCoupon={setSelectedCoupon}
              applyCoupon={applyCoupon}
              couponMessage={couponMessage}
              couponLoading={couponLoading}
              onRemoveCoupon={removeCoupon}
              getTaxBreakdown={getTaxBreakdown}
              getDiscountAmount={getDiscountAmount}
              calculateFinalTotal={calculateFinalTotal}
              onPlaceOrder={handlePlaceOrder}
              onContinueShopping={handleBackToShopping}
              loading={loading}
              showActions={true}
              showTrustBadges={true}
            />
          </div>
        )}

        {/* Address Step */}
        {currentStep === "address" && (
          <AddressPage
            handleBackToCart={handleBackToCart}
            totalItems={totalItems}
            totalAmount={totalAmount}
            deliveryCharge={deliveryData?.deliveryCharge || 0}
            getTaxBreakdown={getTaxBreakdown}
            discount={discount}
            finalTotal={finalTotal}
            getDiscountAmount={getDiscountAmount}
            calculateFinalTotal={calculateFinalTotal}
            cartItems={cartItems}
            // ============ ADD THESE COUPON PROPS ============
            coupons={coupons}
            selectedCoupon={selectedCoupon} // This should now be the object {code, discountPercentage}
            setSelectedCoupon={setSelectedCoupon}
            applyCoupon={applyCoupon}
            couponMessage={couponMessage}
            couponLoading={couponLoading}
            removeCoupon={removeCoupon}
          />
        )}

        {/* Payment Step */}
        {currentStep === "payment" && (
          <div className="payment-content">
            <h2>Payment Method</h2>
            <p>
              This is where you would implement payment options (credit card,
              PayPal, etc.)
            </p>
            <p>For demo purposes, we'll just show a confirmation button</p>

            <div className="payment-summary">
              <h3>Order Total: ₹{finalTotal.toLocaleString()}</h3>
              <button className="btn-confirm-order">Confirm Order</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
